import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'antd';
import AddGalleryPhotoSvg from 'components/_common/Icons/SVGIcons/AddGalleryPhotoSvg';
import CloseCrossSvg from 'components/_common/Icons/SVGIcons/CloseCrossSvg';
import InteractiveStarRating from 'components/_common/InteractiveStarRating';
import Button from 'components/UI/Button';
import { DEFAULT_MOBILE_CODE, MARKETPLACE_SLUG, PRODUCT_SLUG } from 'helpers/DataHelper';
import {
  ALLOW_MAX_FILE_SIZE,
  ALLOWED_IMAGES_AND_VIDEO_TYPES,
  REVIEWS_IMAGE_FOLDER,
} from 'helpers/FileUploadHelper';
import { notify } from 'helpers/NotifyHelper';
import { t } from 'i18next';
import DroppableFileUpload from 'modules/admin/components/_common/DroppableFileUpload';
import { Preloader } from 'modules/admin/components/_common/Preloader';
import { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { uploadMedia } from 'store/auth/actions/AuthAction';
import { userProfileInfoSelector } from 'store/auth/selectors/authSelectors';
import { getProductList } from 'store/customer/actions/CustomerProductAction';
import {
  addFFLReview,
  addMarketplaceReview,
  addProductReview,
} from 'store/customer/actions/CustomerReviewAction';
import { useAppSelector } from 'store/hooks';
import * as Yup from 'yup';

import 'modules/customer/assets/scss/customerAddReviewModal.scss';

// The dropzone copy lists exactly what the picker accepts, so it stays in step with
// ALLOWED_IMAGES_AND_VIDEO_TYPES rather than repeating a hand-written format list.
const MEDIA_FORMAT_LABELS: Record<string, string> = { jpeg: 'JPG', quicktime: 'MOV' };
const SUPPORTED_MEDIA_FORMATS = Array.from(
  new Set(
    ALLOWED_IMAGES_AND_VIDEO_TYPES.map((type) => {
      const subtype = type.split('/')[1] || type;
      return MEDIA_FORMAT_LABELS[subtype] || subtype.toUpperCase();
    })
  )
).join(', ');

interface AddReviewModalProps {
  visible: boolean;
  module: any;
  details: any;
  activeTab?: 'gun_shop' | 'product';
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

export default function AddReviewModal({
  visible,
  module,
  details,
  activeTab,
  onCancel,
  onSubmit,
}: AddReviewModalProps) {
  const userProfileInfo: any = useAppSelector(userProfileInfoSelector);
  const [productOptions, setProductOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);

  // A marketplace review targets the portal itself, so it carries neither an FFL nor a product.
  const isMarketplaceReview = module === MARKETPLACE_SLUG;

  useEffect(() => {
    if (!isMarketplaceReview && visible && details) {
      const fetchProducts = async () => {
        try {
          const fflSlug = details?.slug || details?.ffl?.slug;
          const fflId = details?.id || details?._id || details?.ffl?.id || details?.ffl?._id;
          const filterVal = fflSlug || fflId;

          let response: any;
          if (filterVal) {
            response = await getProductList({
              filters: { ffl: [filterVal] },
              page: 1,
              limit: 100,
            });
          }

          if (
            !response?.status ||
            (!response?.data?.data?.length &&
              !response?.data?.list?.length &&
              !response?.data?.length)
          ) {
            response = await getProductList({ page: 1, limit: 100 });
          }

          if (response?.status) {
            const rawList = Array.isArray(response?.data?.data)
              ? response.data.data
              : Array.isArray(response?.data?.list)
                ? response.data.list
                : Array.isArray(response?.data)
                  ? response.data
                  : [];

            const options = rawList.map((item: any) => ({
              label: item?.product_name || item?.name || item?.title || 'Product',
              value: item?._id || item?.id,
            }));
            setProductOptions(options);
          }
        } catch (error) {
          console.error('Failed to fetch products for review modal:', error);
        }
      };
      fetchProducts();
    }
  }, [visible, details, isMarketplaceReview]);

  const validationSchema = Yup.object().shape({
    rating: Yup.number()
      .required(t('required_error_msg', { field_name: t('rating') }))
      .min(1, t('required_error_msg', { field_name: t('rating') })),
    comment: Yup.string().required(t('required_error_msg', { field_name: t('description') })),
    product_id: Yup.string().nullable(),
    gallery_obj: Yup.array(),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      rating: 0,
      product_id: module === PRODUCT_SLUG ? details?.id || details?._id : undefined,
      comment: '',
      gallery_obj: [],
    },
  });

  const uploadAttachments = async (attachmentFiles: any[]) => {
    if (!attachmentFiles || !attachmentFiles.length) return [];

    const uploadedLinks: string[] = [];
    for (const file of attachmentFiles) {
      if (file?.url && !file?.originFileObj) {
        uploadedLinks.push(file.url);
        continue;
      }

      if (!file?.originFileObj) continue;

      const formData = new FormData();
      formData.append('path', REVIEWS_IMAGE_FOLDER);
      formData.append('file', file.originFileObj);

      const response: any = await uploadMedia(formData);
      if (!response?.status || !response?.data?.links) {
        notify.error(response?.message || t('file_upload_failed_error_msg'));
        return null;
      }
      uploadedLinks.push(response.data.links);
    }
    return uploadedLinks;
  };

  const handleFormSubmit = async (data: any) => {
    setLoading(true);
    try {
      const serverAttachments = await uploadAttachments(data.gallery_obj || []);
      if (serverAttachments === null) {
        setLoading(false);
        return;
      }

      const payload: any = {
        first_name: userProfileInfo?.first_name || '',
        last_name: userProfileInfo?.last_name || '',
        email: userProfileInfo?.email || '',
        mobile_code: Number(userProfileInfo?.mobile_code || DEFAULT_MOBILE_CODE),
        mobile: userProfileInfo?.mobile || '',
        rating: data.rating,
        review: data.comment,
        attachment: serverAttachments,
      };

      let response: any;
      const isGunShopReview = activeTab === 'gun_shop';
      const selectedProductId =
        !isMarketplaceReview &&
        !isGunShopReview &&
        (data.product_id || (module === PRODUCT_SLUG ? details?.id || details?._id : null));

      if (isMarketplaceReview) {
        // Submit as Marketplace Review via POST /marketplace-review
        response = await addMarketplaceReview(payload);
      } else if (selectedProductId) {
        // Submit as Product Review via POST /product-review
        payload.product = selectedProductId;
        response = await addProductReview(payload);
      } else {
        // Submit as FFL Review via POST /ffl-review
        const fflId =
          details?.ffl?.id || details?.ffl?._id || details?.ffl_id || details?.id || details?._id;
        payload.ffl = fflId;
        response = await addFFLReview(payload);
      }

      if (response?.status) {
        notify.success(t('review_added_success_msg', 'Review submitted successfully'));
        onSubmit(data);
        reset();
        onCancel();
      } else {
        notify.error(response?.message || t('something_went_wrong'));
      }
    } catch (error: any) {
      notify.error(error?.message || t('something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  const fflName = isMarketplaceReview
    ? t('mylocalgunshop', 'MyLocalGunShop')
    : details?.business_name || details?.name || details?.title || 'Prolific Arms';

  return (
    <>
      <Preloader loading={loading} />
      <Modal
        show={visible}
        onHide={onCancel}
        centered
        size="lg"
        className="customer-add-review-modal"
        scrollable
      >
        <Modal.Header>
          <div>
            <div className="review-modal-title">{t('write_a_review', 'WRITE A REVIEW')}</div>
            <div className="review-modal-subtitle">
              {t('for', 'For')} <span className="">{fflName}</span>
            </div>
          </div>
          <button
            type="button"
            className="review-modal-close"
            onClick={onCancel}
            aria-label={t('close', 'Close')}
          >
            <CloseCrossSvg />
          </button>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="review-form">
            {/* OVERALL RATING */}
            <div className="overall-rating-section text-center mb-4">
              <div className="overall-rating-label">{t('overall_rating', 'OVERALL RATING')}</div>
              <Controller
                name="rating"
                control={control}
                render={({ field }) => (
                  <InteractiveStarRating
                    value={field.value}
                    onChange={field.onChange}
                    className="star-selection justify-content-center"
                  />
                )}
              />
              {errors.rating && (
                <span className="invalid-feedback d-block text-center mt-1">
                  {(errors.rating as any).message}
                </span>
              )}
            </div>

            {/* Product Name (Optional) */}
            {!isMarketplaceReview && activeTab !== 'gun_shop' && module !== PRODUCT_SLUG && (
              <Form.Group className="mb-3">
                <Form.Label className="text-gray-label mb-1">
                  {t('product_name_optional', 'Product name (optional)')}
                </Form.Label>
                <Controller
                  name="product_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentElement || document.body
                      }
                      placeholder={t('product_name', 'Product Name')}
                      className="w-100 review-product-select"
                      popupClassName="review-product-dropdown-popup"
                      options={productOptions}
                      onChange={(val) => field.onChange(val)}
                      value={field.value || undefined}
                    />
                  )}
                />
              </Form.Group>
            )}

            {/* Write Review */}
            <Form.Group className="mb-3">
              <Form.Label className="text-gray-label mb-1">
                {t('write_review', 'Write Review')}
              </Form.Label>
              <Controller
                name="comment"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    {...field}
                    placeholder={t(
                      'describe_your_experience_placeholder',
                      `Describe your experience with ${fflName}...`
                    )}
                    className={`${errors?.comment ? 'is-invalid' : ''}`}
                  />
                )}
              />
              {errors.comment && (
                <span className="invalid-feedback d-block">{(errors.comment as any).message}</span>
              )}
            </Form.Group>

            {/* Media Upload */}
            <Form.Group className="mb-3">
              <Controller
                name="gallery_obj"
                control={control}
                render={({ field }) => (
                  <DroppableFileUpload
                    {...field}
                    allowedTypes={ALLOWED_IMAGES_AND_VIDEO_TYPES}
                    uploadOnServer={false}
                    imageFolder={REVIEWS_IMAGE_FOLDER}
                    multiple
                    emptyContent={
                      <div className="review-media-dropzone">
                        <span className="review-media-dropzone__icon">
                          <AddGalleryPhotoSvg width={32} height={32} />
                        </span>
                        <p className="review-media-dropzone__title">
                          {t('drag_and_drop_media_or_browse', 'Drag And Drop Media Or Browse')}
                        </p>
                        <p className="review-media-dropzone__hint">
                          {t('review_media_support_note', {
                            defaultValue: `Support For High-Res ${SUPPORTED_MEDIA_FORMATS} Up To ${ALLOW_MAX_FILE_SIZE}MB`,
                            formats: SUPPORTED_MEDIA_FORMATS,
                            maxSize: ALLOW_MAX_FILE_SIZE,
                          })}
                        </p>
                        <span className="review-media-dropzone__btn">
                          {t('upload_from_device', 'Upload from Device')}
                        </span>
                      </div>
                    }
                  />
                )}
              />
            </Form.Group>

            <input type="submit" id="submitFormData" className="d-none" />
          </form>
        </Modal.Body>
        <Modal.Footer className="review-form__actions justify-content-end">
          <Button onClick={() => onCancel()} className="cancel-btn px-4">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="submit-btn px-4"
            onClick={() => document.getElementById('submitFormData')?.click()}
            disabled={loading}
          >
            {loading ? t('submitting', 'Submitting...') : t('post', 'Post')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
